import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

export default class extends Controller {
  static get targets() { return ["list"]; }
  static get values() { return { sortUrl: String, addRowUrl: String, updateUrl: String, addon: Boolean }; }

  // jQuery UI's mouse widget announces a finished drag through its own callback, which no
  // addEventListener - and so no data-action - can see, hence the sortable is set up here. The
  // quick-nav buttons are plain clicks; `_quick_nav` is shared with
  // admin_ranked_model_order_controller.js and can't carry either one's data-action itself, so
  // each mount (see the views) wires the click there instead.
  connect() {
    $(this.listTarget).sortable({
      // Only the .panel-heading child elements support dragging; omit this to make the entire
      // <li>...</li> draggable.
      handle: ".panel-heading",
      update: (event, ui) => this.persist(ui.item[0]),
    });
  }

  disconnect() {
    $(this.listTarget).sortable("destroy");
  }

  onQuickNavClick(event) {
    if (event.target.closest(".to-top")) {
      window.scrollTo(0, 0);
    } else if (event.target.closest(".to-bottom")) {
      window.scrollTo(0, document.body.scrollHeight);
    }
  }

  // Select text to make it easier for new input.
  selectPositionText(event) {
    event.currentTarget.select();
  }

  onPositionKeyup(event) {
    if (event.key === "Enter") {
      this.moveToTypedPosition(event.currentTarget);
    } else if (event.key === "Escape") {
      this.resetInput(event.currentTarget);
    }
  }

  onPositionBlur(event) {
    this.resetInput(event.currentTarget);
  }

  moveToTypedPosition(input) {
    // At this point, index isn't strictly an index, but the value entered by the user. We first
    // check to see if it's a valid position number. If it is, we turn it into a proper 0-index.
    let index = parseInt(input.value);
    if (index < 1) { return; }
    index -= 1;

    const panels = this.panels();
    // Ensure we don't overflow and simply use the last index.
    index = (index + 1 > panels.length) ? panels.length - 1 : index;

    const previousIndex = parseInt(input.dataset.previousIndex);
    const panelToMove = panels[previousIndex];
    const panelToDisplace = panels[index];

    // Determine where to insert the element being moved.
    if (previousIndex < index) {
      panelToDisplace.insertAdjacentElement("afterend", panelToMove);
    } else {
      panelToDisplace.insertAdjacentElement("beforebegin", panelToMove);
    }

    $(this.listTarget).sortable("refresh");
    $(this.listTarget).sortable("refreshPositions");

    this.persist(panelToMove);
  }

  resetInput(input) {
    const previousIndex = parseInt(input.dataset.previousIndex);
    input.setAttribute("value", previousIndex + 1);
    input.value = previousIndex + 1;
  }

  onDescriptionBlur(event) {
    const input = event.currentTarget;
    const li = input.closest("li");
    const method = li.dataset.featureId === undefined ? "POST" : "PATCH";

    const body = new URLSearchParams();
    body.append("id", li.dataset.featureId || "");
    body.append("plan_marketing_feature[description]", input.value);
    body.append("plan_marketing_feature[feature_type]", this.addonValue ? "add_on" : "feature");

    const options = method === "POST" ? RequestHelper.defaultPostOptions() : RequestHelper.defaultPatchOptions();
    options.body = body;

    this.request(this.updateUrlValue, options).
      then(data => {
        window.ltAlert({ message: data.message, type: "toast-success", duration: 2500, lasso: false });
        // `li` refers to the detached old node once this fires - do nothing else with it after.
        // `data.html` is our own server re-rendering the `feature` partial (Haml auto-escapes the
        // interpolated description), not user-supplied markup.
        if (method === "POST") { li.outerHTML = data.html; }
      }).
      catch(this.showErrorToast);
  }

  addFeature() {
    const url = new URL(this.addRowUrlValue, window.location.origin);
    url.searchParams.set("current_index", this.panels().length);

    this.request(url, RequestHelper.defaultRequestOptions()).
      // `data.html` is our own server re-rendering the `feature` partial (Haml auto-escapes the
      // interpolated description), not user-supplied markup.
      then(data => { this.listTarget.insertAdjacentHTML("beforeend", data.html); }).
      catch(this.showErrorToast);
  }

  removeFeature(event) {
    const li = event.currentTarget.closest("li");
    const url = new URL(this.updateUrlValue, window.location.origin);
    url.searchParams.set("id", li.dataset.featureId);

    this.request(url, RequestHelper.defaultDeleteOptions()).
      then(data => {
        li.remove();
        window.ltAlert({ message: data.message, type: "toast-success", duration: 2500, lasso: false });
      }).
      catch(this.showErrorToast);
  }

  persist(movedElement) {
    const panels = this.panels();
    let movedToIndex = 0;
    const body = new URLSearchParams();

    panels.forEach((panel, index) => {
      body.append("order[]", panel.dataset.featureId);
      if (panel === movedElement) { movedToIndex = index; }

      // Keep track of the previous index so we know where to move the new element when using
      // the text input method. Need to update both the value attribute and the DOM value since
      // we rely on the display and the element value.
      const input = panel.querySelector(".feature-inline-position-editor");
      input.dataset.previousIndex = index;
      input.setAttribute("value", index + 1);
      input.value = index + 1;
    });

    const options = RequestHelper.defaultPatchOptions();
    options.body = body;

    this.request(this.sortUrlValue, options).
      then(() => {
        window.ltAlert({
          message: `Feature successfully moved to position ${movedToIndex + 1}.`,
          type: "toast-success",
          duration: 2500,
          lasso: false,
        });
      }).
      catch(this.showErrorToast);
  }

  panels() {
    return Array.from(this.listTarget.querySelectorAll(".panel"));
  }

  // Fetch does not reject on a non-2xx response the way jQuery's $.ajax .fail() does, so the
  // error body has to be read out and re-thrown to reach a single .catch() per call site.
  request(url, options) {
    return fetch(url, options).then(response => (
      response.ok ? response.json() : response.json().then(body => Promise.reject(body))
    ));
  }

  showErrorToast(body) {
    window.ltAlert({ message: body && body.message, type: "toast-error", duration: 2500, lasso: false });
  }
}
